// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EMPTY_VALUE } from "@conklin/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // XNG-Changes
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
});

const mapDispatchToProps = {
    reorder,
};

interface CmsProperties {
    showOrderNumber?: boolean;
    showDate?: boolean;
    showOrderTotal?: boolean;
    showStatus?: boolean;
    showShipTo?: boolean;
    showPONumber?: boolean;
    showReorderProducts?: boolean;
}

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Changes
    cmsProperties?: CmsProperties;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
    actionsCells?: DataTableCellProps;
    reorderButton?: LinkPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderDateText?: TypographyPresentationProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    reorderButtonSpinner: {
        size: 20,
    },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // XNG-Changes
    isReordering,
    isUpdatingCart,
    reorder,
    showAddToCartConfirmationDialog,
    history,
    isVmiOrderHistoryPage,
    cmsProperties,
    showPoNumber,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes
    const toaster = useToaster();
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
        },
    });

    // XNG-Changes
    const shipTo = `${order.stCompanyName} ${order.stAddress1 ? `${order.stAddress1}, ` : ""} ${order.stAddress2 ? `${order.stAddress2}, ` : ""} ${order.shipToCity ? `${order.shipToCity}, ` : ""} ${order.shipToState ? `${order.shipToState}, ` : ""} ${order.shipToPostalCode ? `${order.shipToPostalCode}` : ""}`;

    // XNG-Changes
    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const onReorderError = (message: string) => {
        toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (Object.keys(isReordering).length > 0 || isUpdatingCart) {
            return;
        }
        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };

    // XNG-Changes
    return (
        <>
            <DataTableCell {...{ header: translate("Date") }}>
                {orderNumber ? (
                    <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber} />
                ) : (
                    <Typography {...styles.orderDateText}>{orderDateDisplay}</Typography>
                )}
            </DataTableCell>
            {/* XNG-Changes */}
            {/* <DataTableCell
                {...{ header: translate("Item Code") }}
                {...styles.dataCells}
                className="item-code-grid-item"
            >
                {order?.orderLines?.[0]?.productErpNumber || EMPTY_VALUE}
            </DataTableCell> */}
            {/* XNG-Changes */}
            <DataTableCell {...{ header: translate("Order Number") }} {...styles.dataCells}>
                {orderNumber ? (
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={orderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                ) : (
                    EMPTY_VALUE
                )}
            </DataTableCell>
            {/* XNG-Changes */}
            {/* <DataTableCell {...{ header: translate("SKU") }} {...styles.dataCells} className="sku-grid-item">
                {order?.orderLines?.[0]?.properties?.sku || EMPTY_VALUE}
            </DataTableCell> */}
            {/* XNG-Changes */}
            {/* <DataTableCell {...{ header: translate("Product Name") }} {...styles.dataCells}>
                <Link href={order?.orderLines?.[0]?.productUri}>{order?.orderLines?.[0]?.description}</Link>
            </DataTableCell> */}
            {/* XNG-Changes */}
            <DataTableCell {...{ header: translate("Ship-To/Pickup") }} {...styles.dataCells}>
                {shipTo}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Price") }} {...styles.dataCells}>
                {order?.orderLines?.[0]?.unitNetPriceDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Reorder") }} {...styles.actionsCells}>
                {isReordering[orderNumber] ? (
                    <StyledWrapper style={{ width: "62px", textAlign: "center" }}>
                        <LoadingSpinner {...styles.reorderButtonSpinner} />
                    </StyledWrapper>
                ) : (
                    <Link
                        {...styles.reorderButton}
                        disabled={Object.keys(isReordering).length > 0 || isUpdatingCart || !order.erpOrderNumber}
                        onClick={() => reorderClick(orderNumber, orderNumber, !!order?.vmiLocationId)}
                        data-test-selector="orderHistoryTable_Reorder"
                        aria-label="Reorder"
                    >
                        {translate("Reorder")}
                    </Link>
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderSummaryCard));
